import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Pill, Avatar, timeAgo } from "@/components/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Users, BookText, Plug, Lock, ScrollText, ShieldAlert, Plus, GraduationCap, ExternalLink, CheckCircle2, LogIn } from "lucide-react";

const TABS = [
  { id: "users", label: "Users", icon: Users },
  { id: "content", label: "Content", icon: BookText },
  { id: "classroom", label: "Classroom", icon: GraduationCap },
  { id: "integrations", label: "Integrations", icon: Plug },
  { id: "firm", label: "Firm Settings", icon: Lock },
  { id: "signins", label: "Sign-ins", icon: LogIn },
  { id: "audit", label: "Audit Log", icon: ScrollText },
];

export default function Admin() {
  const [tab, setTab] = useState("users");
  return (
    <div className="p-8 md:p-10">
      <PageHeader title="Admin" subtitle="Users, content, integrations, the locked Firm Settings, and an immutable audit log." />
      <div className="flex flex-wrap gap-2 mb-6 border-b border-[#BDC5DB]/60">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-t-lg -mb-px border-b-2 transition-all ${tab === t.id ? "border-[#FFD100] text-[#141B4D]" : "border-transparent text-[#101820]/50 hover:text-[#141B4D]"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "users" && <UsersTab />}
      {tab === "content" && <ContentTab />}
      {tab === "classroom" && <ClassroomTab />}
      {tab === "integrations" && <IntegrationsTab />}
      {tab === "firm" && <FirmTab />}
      {tab === "signins" && <SigninsTab />}
      {tab === "audit" && <AuditTab />}
    </div>
  );
}

const ROLE_TONE = { admin: "yellow", attorney: "red", reviewer: "cobalt", teammate: "green" };

function UsersTab() {
  const [users, setUsers] = useState([]);
  useEffect(() => { api.get("/users").then(({ data }) => setUsers(data)); }, []);
  return (
    <div className="brand-fade bg-white rounded-xl overflow-hidden shadow-sm border border-black/5">
      <table className="w-full text-sm">
        <thead><tr className="bg-[#141B4D] text-[#F2E9DB] text-left"><th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Title</th><th className="p-4 font-medium">Role</th></tr></thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="even:bg-[#F2E9DB] odd:bg-white border-t border-[#BDC5DB]/30">
              <td className="p-4"><div className="flex items-center gap-2"><Avatar name={u.name} size={28} /><span className="font-semibold text-[#141B4D]">{u.name}</span></div></td>
              <td className="p-4 text-[#101820]/70">{u.email}</td>
              <td className="p-4 text-[#101820]/70">{u.title}</td>
              <td className="p-4"><Pill tone={ROLE_TONE[u.role]}>{u.role}</Pill></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ContentTab() {
  const [docs, setDocs] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = async () => setDocs((await api.get("/knowledge")).data);
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = { type: editing.type, title: editing.title, body: editing.body, tags: editing.tags || [], source: editing.source || "Manual" };
    if (editing.id) await api.put(`/knowledge/${editing.id}`, payload);
    else await api.post("/knowledge", payload);
    toast.success("Knowledge saved");
    setEditing(null); load();
  };

  return (
    <div className="brand-fade">
      <div className="flex justify-between items-center mb-4">
        <div className="callout rounded-lg p-3 text-sm flex items-center gap-2"><ShieldAlert className="w-4 h-4 text-[#141B4D]" /> Edit the Knowledge Layer — never hardcode procedures, fees, or templates.</div>
        <Button data-testid="new-knowledge" onClick={() => setEditing({ type: "faq", title: "", body: "", tags: [], source: "Manual" })} className="bg-[#1D57A5] text-white hover:bg-[#141B4D]"><Plus className="w-4 h-4 mr-1" /> New</Button>
      </div>

      {editing && (
        <div className="bg-white rounded-xl p-5 shadow-md border-l-4 border-[#1D57A5] mb-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Input data-testid="edit-title" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Title" className="bg-[#F2E9DB] border-[#BDC5DB]" />
            <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })} className="rounded-lg bg-[#F2E9DB] border border-[#BDC5DB] px-3">
              <option value="faq">FAQ</option><option value="sop">SOP</option><option value="glossary">Glossary</option><option value="template">Template</option>
            </select>
          </div>
          <Textarea data-testid="edit-body" value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} rows={4} placeholder="Body" className="bg-[#F2E9DB] border-[#BDC5DB]" />
          <div className="flex gap-2">
            <Button data-testid="save-knowledge" onClick={save} className="bg-[#FFD100] text-[#101820] font-bold hover:brightness-95">Save</Button>
            <Button variant="outline" onClick={() => setEditing(null)} className="border-[#BDC5DB]">Cancel</Button>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {docs.map((d) => (
          <div key={d.id} className="bg-white rounded-xl p-4 shadow-sm border border-black/5">
            <div className="flex items-start justify-between gap-2">
              <div className="font-bold text-[#141B4D]">{d.title}</div>
              <Pill tone="cobalt">{d.type.toUpperCase()}</Pill>
            </div>
            <p className="text-sm text-[#101820]/70 mt-1 line-clamp-2">{d.body}</p>
            <Button data-testid={`edit-${d.id}`} variant="outline" onClick={() => setEditing(d)} className="mt-2 h-8 text-xs border-[#1D57A5] text-[#1D57A5] hover:bg-[#1D57A5] hover:text-white">Edit</Button>
          </div>
        ))}
      </div>
    </div>
  );
}

const FRESH_MAP = {
  fresh: { label: "Fresh", bg: "#E3F0EA", color: "#006747" },
  review_due: { label: "Review due", bg: "#FBF3D0", color: "#B45309" },
  stale: { label: "Stale", bg: "#FBE0E0", color: "#B91C1C" },
  unknown: { label: "Not reviewed", bg: "#BDC5DB40", color: "#141B4D" },
};

function ClassroomTab() {
  const [rows, setRows] = useState([]);
  const load = async () => setRows((await api.get("/classroom/needs-review")).data);
  useEffect(() => { load(); }, []);
  const markReviewed = async (id) => { await api.post(`/classroom/lessons/${id}/reviewed`); toast.success("Marked reviewed — freshness reset"); load(); };
  const flagged = rows.filter((r) => r.status !== "fresh" || r.uspto_dependent).length;

  return (
    <div className="brand-fade">
      <div className="callout rounded-lg p-3 text-sm mb-4 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-[#141B4D]" />
        <span><span className="font-bold">Onboarding content freshness.</span> {flagged} lesson(s) need attention. USPTO-dependent lessons are surfaced first — verify them against the primary source and mark reviewed to reset the clock.</span>
      </div>
      <div className="bg-white rounded-xl overflow-hidden shadow-sm border border-black/5">
        <table className="w-full text-sm">
          <thead><tr className="bg-[#141B4D] text-[#F2E9DB] text-left">
            <th className="p-4 font-medium">Lesson</th><th className="p-4 font-medium">Chapter</th>
            <th className="p-4 font-medium">Freshness</th><th className="p-4 font-medium">USPTO</th>
            <th className="p-4 font-medium">Last reviewed</th><th className="p-4 font-medium">Owner</th><th className="p-4 font-medium"></th>
          </tr></thead>
          <tbody>
            {rows.map((r) => {
              const f = FRESH_MAP[r.status] || FRESH_MAP.unknown;
              return (
                <tr key={r.lesson_id} data-testid={`needs-review-${r.lesson_id}`} className="even:bg-[#F2E9DB] odd:bg-white border-t border-[#BDC5DB]/30">
                  <td className="p-4 font-semibold text-[#141B4D]">{r.title}</td>
                  <td className="p-4 text-[#101820]/70">{r.chapter}</td>
                  <td className="p-4"><span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold" style={{ background: f.bg, color: f.color }}>{f.label}{r.days != null ? ` · ${r.days}d` : ""}</span></td>
                  <td className="p-4">{r.uspto_dependent ? <Pill tone="cobalt">Yes</Pill> : <span className="text-[#101820]/30">—</span>}</td>
                  <td className="p-4 text-[#101820]/60">{r.last_reviewed || "—"}{r.reviewed_by ? ` · ${r.reviewed_by}` : ""}</td>
                  <td className="p-4 text-[#101820]/60">{r.owner?.split("@")[0]}</td>
                  <td className="p-4"><Button data-testid={`mark-reviewed-${r.lesson_id}`} onClick={() => markReviewed(r.lesson_id)} className="h-8 text-xs bg-[#006747] text-white hover:brightness-110"><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark reviewed</Button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function IntegrationsTab() {
  const [items, setItems] = useState([]);
  const [hs, setHs] = useState(null);
  const [gg, setGg] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncingG, setSyncingG] = useState(false);
  const loadHs = () => api.get("/hubspot/status").then(({ data }) => setHs(data)).catch(() => {});
  const loadGg = () => api.get("/google/status").then(({ data }) => setGg(data)).catch(() => {});
  useEffect(() => { api.get("/integrations").then(({ data }) => setItems(data)); loadHs(); loadGg(); }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/hubspot/sync");
      setHs(data);
      const s = data.stats || {};
      toast.success(`HubSpot synced — ${s.matters} matters, ${s.contacts} contacts, ${s.deals} deals`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "HubSpot sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const syncG = async () => {
    setSyncingG(true);
    try {
      const { data } = await api.post("/google/sync");
      if (data.already_running) {
        toast.info("A refresh is already running…");
      } else {
        toast.success("Refresh started — scanning Gmail + Drive. This can take a minute.");
      }
      // Poll status until the background sync finishes.
      const poll = setInterval(async () => {
        try {
          const { data: s } = await api.get("/google/status");
          setGg(s);
          if (!s.syncing) {
            clearInterval(poll);
            setSyncingG(false);
            if (s.error) toast.error(`Refresh failed: ${s.error}`);
            else toast.success(`Front Desk refreshed — ${s.front_desk} client threads waiting`);
          }
        } catch { /* keep polling */ }
      }, 4000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Google sync failed");
      setSyncingG(false);
    }
  };

  return (
    <div className="brand-fade space-y-4">
      {hs?.enabled && (
        <div data-testid="hubspot-sync-panel" className="bg-[#141B4D] text-white rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <Plug className="w-4 h-4 text-[#FFD100]" /> HubSpot — Live sync
            </div>
            <p className="text-sm text-white/60 mt-1">
              {hs.stats ? `${hs.stats.matters} matters · ${hs.stats.contacts} contacts · ${hs.stats.deals} deals` : "No sync yet."}
              {hs.last_sync && <span className="ml-2 text-white/40">Last synced {timeAgo(hs.last_sync)}</span>}
              {hs.error && <span className="ml-2 text-[#FCA5A5]">Error: {hs.error}</span>}
            </p>
            <p className="text-xs text-white/40 mt-0.5">Auto-refreshes daily · sync manually anytime.</p>
          </div>
          <Button data-testid="hubspot-sync-btn" onClick={sync} disabled={syncing}
            className="bg-[#FFD100] text-[#101820] font-bold hover:brightness-95 shrink-0">
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </div>
      )}
      {gg?.enabled && (
        <div data-testid="google-sync-panel" className="bg-[#006747] text-white rounded-xl p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 font-bold">
              <Plug className="w-4 h-4 text-[#FFD100]" /> Google (Gmail + Drive) — Front Desk
            </div>
            <p className="text-sm text-white/70 mt-1">
              Reading {gg.impersonating}. {gg.front_desk != null ? `${gg.front_desk} client threads waiting on us.` : ""}
              {gg.slack?.enabled && <span className="ml-1">Slack: {gg.slack.channels ?? 0} channels.</span>}
              {gg.last_sync && <span className="ml-2 text-white/50">Refreshed {timeAgo(gg.last_sync)}</span>}
              {gg.error && <span className="ml-2 text-[#FCA5A5]">Error: {gg.error}</span>}
            </p>
            <p className="text-xs text-white/50 mt-0.5">Auto-refreshes daily · refresh manually anytime.</p>
          </div>
          <Button data-testid="google-sync-btn" onClick={syncG} disabled={syncingG}
            className="bg-[#FFD100] text-[#101820] font-bold hover:brightness-95 shrink-0">
            {syncingG ? "Refreshing…" : "Refresh Front Desk"}
          </Button>
        </div>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <div key={it.id} className="bg-white rounded-xl p-4 shadow-sm border border-black/5">
            <div className="flex items-center justify-between">
              <div className="font-bold text-[#141B4D]">{it.name}</div>
              <Pill tone={it.status.toLowerCase().includes("live") ? "green" : "pale"}>{it.status}</Pill>
            </div>
            <p className="text-sm text-[#101820]/60 mt-1">{it.desc}</p>
            <div className="text-xs text-[#101820]/40 mt-2">Scope: {it.scope}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FirmTab() {
  const [firm, setFirm] = useState(null);
  useEffect(() => { api.get("/firm-settings").then(({ data }) => setFirm(data)); }, []);
  if (!firm) return null;
  return (
    <div className="brand-fade max-w-3xl space-y-5">
      <div className="rounded-lg bg-[#FBE0E0] border border-[#B91C1C]/20 p-3 flex items-center gap-2 text-sm text-[#B91C1C]">
        <Lock className="w-4 h-4" /> {firm.warning}
      </div>
      <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
        <h3 className="font-bold text-[#141B4D] mb-3">Fee Schedule (firm fees)</h3>
        <table className="w-full text-sm">
          <tbody>
            {firm.fee_schedule.map((f, i) => (
              <tr key={i} className="even:bg-[#F2E9DB] border-t border-[#BDC5DB]/30">
                <td className="p-3">{f.item}</td><td className="p-3 text-right font-semibold text-[#006747]">{f.fee}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {firm.gov_fee_notice && (
          <div className="mt-3 rounded-lg bg-[#FBF3D0] border border-[#FFD100] p-3 text-sm text-[#8a6d00]">
            {firm.gov_fee_notice}
            {firm.gov_fee_source && (
              <a href={firm.gov_fee_source.url} target="_blank" rel="noreferrer" data-testid="gov-fee-link"
                className="mt-1.5 inline-flex items-center gap-1.5 font-semibold text-[#1D57A5] hover:underline">
                {firm.gov_fee_source.label} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        )}
        {firm.firm_fee_notice && <p className="text-xs text-[#101820]/50 mt-2">{firm.firm_fee_notice}</p>}
      </div>
      {firm.review_chain && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
          <h3 className="font-bold text-[#141B4D] mb-1.5">Review & Approval Chain</h3>
          <p className="text-sm text-[#101820]/75">{firm.review_chain}</p>
        </div>
      )}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
          <h3 className="font-bold text-[#141B4D] mb-2">Firm Address</h3>
          <p className="text-sm text-[#101820]/70">{firm.firm_address}</p>
          <h3 className="font-bold text-[#141B4D] mb-2 mt-4">Specimen Boilerplate</h3>
          <p className="text-sm text-[#101820]/70 italic">"{firm.specimen_boilerplate}"</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-black/5">
          <h3 className="font-bold text-[#141B4D] mb-2">Attorneys & Bar Details</h3>
          {firm.attorneys.map((a, i) => (
            <div key={i} className="flex justify-between items-start text-sm py-1.5 border-b border-[#BDC5DB]/30 last:border-0 gap-2">
              <div><span className="text-[#141B4D] font-medium">{a.name}</span>{a.role && <div className="text-[11px] text-[#101820]/50">{a.role}</div>}</div>
              <span className="font-mono text-[#101820]/60 shrink-0">{a.bar}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AuditTab() {
  const [log, setLog] = useState([]);
  useEffect(() => { api.get("/audit-log").then(({ data }) => setLog(data)); }, []);
  return (
    <div className="brand-fade bg-white rounded-xl overflow-hidden shadow-sm border border-black/5">
      <div className="callout p-3 text-xs">Immutable audit log — visible to admins only.</div>
      <table className="w-full text-sm">
        <thead><tr className="bg-[#141B4D] text-[#F2E9DB] text-left"><th className="p-4 font-medium">Actor</th><th className="p-4 font-medium">Action</th><th className="p-4 font-medium">Detail</th><th className="p-4 font-medium">When</th></tr></thead>
        <tbody>
          {log.map((l) => (
            <tr key={l.id} className="even:bg-[#F2E9DB] odd:bg-white border-t border-[#BDC5DB]/30">
              <td className="p-4 font-medium text-[#141B4D]">{l.actor_name}</td>
              <td className="p-4 text-[#101820]/80">{l.action}</td>
              <td className="p-4 text-[#101820]/50 font-mono text-xs truncate max-w-xs">{l.detail}</td>
              <td className="p-4 text-[#101820]/50">{timeAgo(l.at)}</td>
            </tr>
          ))}
          {log.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#101820]/40">No activity logged yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function SigninsTab() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/signin-audit").then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return (
    <div data-testid="signins-tab" className="brand-fade bg-white rounded-xl overflow-hidden shadow-sm border border-black/5">
      <div className="callout p-3 text-xs">Recent Google sign-in attempts (last 100). Use this to diagnose login issues without server logs.</div>
      <table className="w-full text-sm">
        <thead><tr className="bg-[#141B4D] text-[#F2E9DB] text-left"><th className="p-4 font-medium">Result</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Reason</th><th className="p-4 font-medium">When</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} data-testid={`signin-row-${r.id}`} className="even:bg-[#F2E9DB] odd:bg-white border-t border-[#BDC5DB]/30">
              <td className="p-4"><Pill tone={r.outcome === "success" ? "green" : "red"}>{r.outcome}</Pill></td>
              <td className="p-4 font-medium text-[#141B4D]">{r.email || <span className="text-[#101820]/40">unknown</span>}</td>
              <td className="p-4 text-[#101820]/60 font-mono text-xs">{r.reason}</td>
              <td className="p-4 text-[#101820]/50">{timeAgo(r.at)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-[#101820]/40">No sign-in attempts logged yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}
